import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as SQLite from 'expo-sqlite';
import { webStorage } from './WebStorage';

// Data classification for storage strategy
export enum StorageStrategy {
  LOCAL_STORAGE = 'localStorage',     // Simple key-value data
  SQLITE = 'sqlite',                  // Complex relational data
  BOTH = 'both'                       // Hybrid with cache layer
}

// Configuration for each data type
export interface DataTypeConfig {
  strategy: StorageStrategy;
  table?: string;                     // SQLite table name
  key?: string;                       // localStorage key prefix
  cacheDuration?: number;             // Cache TTL in minutes
  syncPriority?: number;              // 1-5, higher = more important
  requiresRelations?: boolean;        // Has foreign keys
}

// Smart data classification
export const DATA_TYPE_CONFIGS: Record<string, DataTypeConfig> = {
  // Simple data - localStorage only
  userPreferences: {
    strategy: StorageStrategy.LOCAL_STORAGE,
    key: 'user_prefs',
    cacheDuration: 1440 // 24 hours
  },
  appSettings: {
    strategy: StorageStrategy.LOCAL_STORAGE,
    key: 'app_settings',
    cacheDuration: 1440
  },
  tempFormData: {
    strategy: StorageStrategy.LOCAL_STORAGE,
    key: 'temp_forms',
    cacheDuration: 60 // 1 hour
  },
  
  // Simple lists - localStorage with SQLite backup
  vehicles: {
    strategy: StorageStrategy.BOTH,
    table: 'vehicles',
    key: 'vehicles_cache',
    cacheDuration: 30,
    syncPriority: 3,
    requiresRelations: false
  },
  drivers: {
    strategy: StorageStrategy.BOTH,
    table: 'drivers', 
    key: 'drivers_cache',
    cacheDuration: 30,
    syncPriority: 3,
    requiresRelations: false
  },
  
  // Complex relational data - SQLite primary
  assignments: {
    strategy: StorageStrategy.SQLITE,
    table: 'assignment_history',
    syncPriority: 5,
    requiresRelations: true
  },
  assignment_history: {
    strategy: StorageStrategy.SQLITE,
    table: 'assignment_history',
    syncPriority: 5,
    requiresRelations: true
  },
  receivingForms: {
    strategy: StorageStrategy.SQLITE,
    table: 'vehicle_receiving_forms',
    syncPriority: 4,
    requiresRelations: true
  },
  deliveryForms: {
    strategy: StorageStrategy.SQLITE,
    table: 'vehicle_delivery_forms',
    syncPriority: 4,
    requiresRelations: true
  },
  damages: {
    strategy: StorageStrategy.SQLITE,
    table: 'vehicle_damages',
    syncPriority: 3,
    requiresRelations: true
  },
  accounting: {
    strategy: StorageStrategy.SQLITE,
    table: 'accounting_transactions',
    syncPriority: 5,
    requiresRelations: true
  }
};

export class HybridStorageService {
  private db: SQLite.SQLiteDatabase | null = null;
  private isInitialized = false;
  private sqliteUnavailableLogged = new Set<string>(); // Track which tables have been logged

  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      // Initialize SQLite for complex data (only if supported)
      if (Platform.OS !== 'web' && typeof window === 'undefined') {
        // React Native environment - use SQLite
        this.db = await SQLite.openDatabaseAsync('stsc_hybrid.db');
        await this.createTables();
        console.log('🔄 SQLite initialized for React Native');
      } else {
        // Web environment - SQLite not supported, will use localStorage only
        console.log('🌐 Web environment detected - using localStorage only');
      }
      
      console.log('🔄 Hybrid storage initialized successfully');
      this.isInitialized = true;
    } catch (error) {
      console.warn('⚠️ SQLite initialization failed, falling back to localStorage only:', error);
      this.isInitialized = true; // Still mark as initialized to continue with localStorage
    }
  }

  private async createTables(): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');

    // Create minimal SQLite tables for complex data only
    const tables = `
      CREATE TABLE IF NOT EXISTS assignment_history (
        id TEXT PRIMARY KEY,
        vehicle_id TEXT NOT NULL,
        primary_driver_id TEXT NOT NULL,
        secondary_driver_id TEXT,
        assignment_date TEXT NOT NULL,
        return_date TEXT,
        status TEXT DEFAULT 'active',
        is_shared TEXT DEFAULT 'false',
        assignment_type TEXT DEFAULT 'rental',
        sync_status TEXT DEFAULT 'pending',
        created_at TEXT DEFAULT CURRENT_TIMESTAMP,
        updated_at TEXT DEFAULT CURRENT_TIMESTAMP
      );

      CREATE TABLE IF NOT EXISTS vehicle_receiving_forms (
        id TEXT PRIMARY KEY,
        assignment_id TEXT,
        reference_number TEXT,
        vehicle_id TEXT NOT NULL,
        driver_id TEXT NOT NULL,
        assignment_date TEXT,
        assignment_time TEXT,
        sync_status TEXT DEFAULT 'pending',
        created_at TEXT DEFAULT CURRENT_TIMESTAMP,
        FOREIGN KEY (assignment_id) REFERENCES assignment_history(id)
      );

      CREATE TABLE IF NOT EXISTS vehicle_delivery_forms (
        id TEXT PRIMARY KEY,
        assignment_id TEXT,
        driver_id TEXT NOT NULL,
        vehicle_id TEXT NOT NULL,
        delivery_date TEXT,
        delivery_time TEXT,
        final_mileage INTEGER,
        fuel_level INTEGER,
        exterior_condition TEXT,
        interior_condition TEXT,
        tire_condition TEXT,
        has_all_keys BOOLEAN DEFAULT true,
        has_all_documents BOOLEAN DEFAULT true,
        handed_over_by TEXT,
        received_by TEXT,
        additional_notes TEXT,
        sync_status TEXT DEFAULT 'pending',
        created_at TEXT DEFAULT CURRENT_TIMESTAMP,
        FOREIGN KEY (assignment_id) REFERENCES assignment_history(id)
      );

      CREATE TABLE IF NOT EXISTS vehicle_damages (
        id TEXT PRIMARY KEY,
        receiving_form_id TEXT,
        delivery_form_id TEXT,
        damage_type TEXT NOT NULL,
        damage_severity TEXT NOT NULL,
        damage_location TEXT,
        damage_description TEXT,
        x_coordinate REAL,
        y_coordinate REAL,
        photo_url TEXT,
        sync_status TEXT DEFAULT 'pending',
        created_at TEXT DEFAULT CURRENT_TIMESTAMP,
        FOREIGN KEY (receiving_form_id) REFERENCES vehicle_receiving_forms(id),
        FOREIGN KEY (delivery_form_id) REFERENCES vehicle_delivery_forms(id)
      );

      CREATE TABLE IF NOT EXISTS sync_metadata (
        table_name TEXT PRIMARY KEY,
        last_sync TEXT,
        sync_version INTEGER DEFAULT 1
      );
    `;

    await this.db.execAsync(tables);
  }

  // Smart storage - automatically chooses storage method
  async store(dataType: string, data: any, id?: string): Promise<void> {
    const config = DATA_TYPE_CONFIGS[dataType];
    if (!config) throw new Error(`Unknown data type: ${dataType}`);

    switch (config.strategy) {
      case StorageStrategy.LOCAL_STORAGE:
        await this.storeInLocalStorage(config.key!, data, id);
        break;
        
      case StorageStrategy.SQLITE:
        await this.storeInSQLite(config.table!, data);
        break;
        
      case StorageStrategy.BOTH:
        // Store in both with localStorage as cache
        await Promise.all([
          this.storeInLocalStorage(config.key!, data, id),
          this.storeInSQLite(config.table!, data)
        ]);
        break;
    }
  }

  // Smart retrieval - tries cache first, falls back to SQLite
  async retrieve(dataType: string, id?: string): Promise<any> {
    const config = DATA_TYPE_CONFIGS[dataType];
    if (!config) throw new Error(`Unknown data type: ${dataType}`);

    switch (config.strategy) {
      case StorageStrategy.LOCAL_STORAGE:
        return await this.getFromLocalStorage(config.key!, id);
        
      case StorageStrategy.SQLITE:
        return await this.getFromSQLite(config.table!, id);
        
      case StorageStrategy.BOTH:
        // Try localStorage cache first
        try {
          const cached = await this.getFromLocalStorage(config.key!, id);
          if (cached && this.isCacheValid(cached, config.cacheDuration!)) {
            // For getAll requests (no id), return the cached array data
            if (!id && Array.isArray(cached.data)) {
              console.log(`📦 Retrieved ${cached.data.length} ${dataType} items from cache`);
              return cached.data;
            }
            // For single item requests, return the item
            if (id) {
              return cached.data;
            }
          }
        } catch (error) {
          console.log(`Cache miss for ${dataType}, trying SQLite...`);
        }
        
        // Fallback to SQLite
        const sqliteData = await this.getFromSQLite(config.table!, id);
        
        // Update cache
        if (sqliteData) {
          await this.storeInLocalStorage(config.key!, sqliteData, id);
        }
        
        return sqliteData;
    }
  }

  // localStorage operations
  private async storeInLocalStorage(keyPrefix: string, data: any, id?: string): Promise<void> {
    try {
      const key = id ? `${keyPrefix}_${id}` : keyPrefix;
      const payload = {
        data,
        timestamp: Date.now(),
        version: 1
      };
      
      await webStorage.setItem(key, JSON.stringify(payload));
    } catch (error) {
      console.error('❌ localStorage store error:', error);
      throw error;
    }
  }

  private async getFromLocalStorage(keyPrefix: string, id?: string): Promise<any> {
    try {
      const key = id ? `${keyPrefix}_${id}` : keyPrefix;
      const stored = await webStorage.getItem(key);
      
      if (!stored) return null;
      
      const parsed = JSON.parse(stored);
      return parsed;
    } catch (error) {
      console.error('❌ localStorage retrieve error:', error);
      return null;
    }
  }

  // SQLite operations (for complex data)
  private async storeInSQLite(tableName: string, data: any): Promise<void> {
    if (!this.db) {
      // No SQLite available, fall back to localStorage
      // Reduced logging to prevent console spam - only log once per table
      if (!this.sqliteUnavailableLogged.has(tableName)) {
        console.log(`🌐 SQLite not available for ${tableName}, using localStorage fallback`);
        this.sqliteUnavailableLogged.add(tableName);
      }
      await this.storeInLocalStorage(`sqlite_${tableName}`, data, data.id);
      return;
    }

    try {
      // Dynamic insert based on data properties
      const columns = Object.keys(data).join(', ');
      const placeholders = Object.keys(data).map(() => '?').join(', ');
      const values = Object.values(data);

      const query = `
        INSERT OR REPLACE INTO ${tableName} (${columns})
        VALUES (${placeholders})
      `;

      await this.db.runAsync(query, values as any[]);
    } catch (error) {
      console.error(`❌ SQLite store error for ${tableName}:`, error);
      // Fall back to localStorage
      await this.storeInLocalStorage(`sqlite_${tableName}`, data, data.id);
    }
  }

  private async getFromSQLite(tableName: string, id?: string): Promise<any> {
    if (!this.db) {
      // No SQLite available, fall back to localStorage
      // Reduced logging to prevent console spam - only log once per table for reads
      if (!this.sqliteUnavailableLogged.has(`read_${tableName}`)) {
        console.log(`🌐 SQLite not available for reading ${tableName}, using localStorage fallback`);
        this.sqliteUnavailableLogged.add(`read_${tableName}`);
      }
      
      if (id) {
        return await this.getFromLocalStorage(`sqlite_${tableName}`, id);
      } else {
        // For getAll, try to get all keys matching the pattern
        const keys = await webStorage.getAllKeys();
        const matchingKeys = keys.filter(key => key.startsWith(`sqlite_${tableName}_`));
        const results = [];
        
        for (const key of matchingKeys) {
          const stored = await webStorage.getItem(key);
          if (stored) {
            try {
              const parsed = JSON.parse(stored);
              if (parsed.data) {
                results.push(parsed.data);
              }
            } catch (e) {
              console.warn('Error parsing stored data:', e);
            }
          }
        }
        return results;
      }
    }

    try {
      let query = `SELECT * FROM ${tableName}`;
      let params: any[] = [];

      if (id) {
        query += ' WHERE id = ?';
        params = [id];
        const result = await this.db.getFirstAsync(query, params);
        return result || null;
      } else {
        const results = await this.db.getAllAsync(query);
        return results || [];
      }
    } catch (error) {
      console.error(`❌ SQLite retrieve error for ${tableName}:`, error);
      // Fall back to localStorage
      return await this.getFromLocalStorage(`sqlite_${tableName}`, id);
    }
  }

  // Cache validation
  private isCacheValid(cached: any, durationMinutes: number): boolean {
    if (!cached || !cached.timestamp) return false;
    
    const now = Date.now();
    const ageMinutes = (now - cached.timestamp) / (1000 * 60);
    
    return ageMinutes < durationMinutes;
  }

  // Utility methods
  async getAllPendingSync(): Promise<Record<string, any[]>> {
    const pendingData: Record<string, any[]> = {};

    // Get SQLite pending items
    if (this.db) {
      for (const [dataType, config] of Object.entries(DATA_TYPE_CONFIGS)) {
        if (config.strategy === StorageStrategy.SQLITE || config.strategy === StorageStrategy.BOTH) {
          try {
            const result = await this.db.getAllAsync(
              `SELECT * FROM ${config.table} WHERE sync_status = 'pending'`
            );
            if (result.length > 0) {
              pendingData[dataType] = result;
            }
          } catch (error) {
            console.error(`Error getting pending ${dataType}:`, error);
          }
        }
      }
    }

    return pendingData;
  }

  async clearCache(dataType?: string): Promise<void> {
    if (dataType) {
      const config = DATA_TYPE_CONFIGS[dataType];
      if (config?.key) {
        await AsyncStorage.removeItem(config.key);
      }
    } else {
      // Clear all localStorage cache
      const keys = await AsyncStorage.getAllKeys();
      const cacheKeys = keys.filter(key => 
        Object.values(DATA_TYPE_CONFIGS).some(config => 
          config.key && key.startsWith(config.key)
        )
      );
      await AsyncStorage.multiRemove(cacheKeys);
    }
  }

  async getStorageStats(): Promise<any> {
    const stats = {
      localStorage: {},
      sqlite: {},
      total: {
        localStorageItems: 0,
        sqliteRows: 0,
        pendingSync: 0
      }
    };

    // Count localStorage items
    const keys = await AsyncStorage.getAllKeys();
    stats.total.localStorageItems = keys.length;

    // Count SQLite rows
    if (this.db) {
      for (const [dataType, config] of Object.entries(DATA_TYPE_CONFIGS)) {
        if (config.table) {
          try {
            const result = await this.db.getAllAsync(
              `SELECT COUNT(*) as count FROM ${config.table}`
            );
            const count = (result[0] as any)?.count || 0;
            (stats.sqlite as any)[dataType] = count;
            stats.total.sqliteRows += count;

            // Count pending sync
            const pendingResult = await this.db.getAllAsync(
              `SELECT COUNT(*) as count FROM ${config.table} WHERE sync_status = 'pending'`
            );
            stats.total.pendingSync += (pendingResult[0] as any)?.count || 0;
          } catch (error) {
            console.error(`Error counting ${dataType}:`, error);
          }
        }
      }
    }

    return stats;
  }

  // Helper method to get data type configuration
  getDataTypeConfig(dataType: string): DataTypeConfig | undefined {
    return DATA_TYPE_CONFIGS[dataType];
  }

  // Method to store complete arrays (for getAll operations)
  async storeArray(dataType: string, items: any[]): Promise<void> {
    const config = DATA_TYPE_CONFIGS[dataType];
    if (!config?.key) {
      console.warn(`No cache key configured for ${dataType}`);
      return;
    }

    try {
      // Store the complete array with cache metadata
      const payload = {
        data: items,
        timestamp: Date.now(),
        itemCount: items.length
      };
      
      await webStorage.setItem(config.key, JSON.stringify(payload));
      console.log(`📦 Stored ${items.length} ${dataType} items in cache`);
    } catch (error) {
      console.error(`❌ Error storing ${dataType} array:`, error);
    }
  }
}

export const hybridStorage = new HybridStorageService();